/**
 * Human-readable question number for cards and lists.
 *
 * APIs may send `question_index` as 0-based (0,1,2,…) or 1-based (1,2,3,…).
 * When array order matches that encoding, we infer 0-based vs 1-based from
 * `min(question_index)` across the full list when `allQuestions` is provided.
 *
 * When `randomize_questions` shuffles the array but `question_index` stays the
 * stable 0-based slot, the legacy `n === i + 1` branch would misfire (e.g. index 2
 * at array position 1 looks like "1-based aligned" and shows 2 instead of 3),
 * producing duplicate labels like 1,2,2,4.
 *
 * @param {object} item - Question object, may include `question_index`
 * @param {number} arrayIndex - Zero-based index in the current list (from map)
 * @param {object[]|undefined} allQuestions - Full question list for 0-based detection
 * @returns {number}
 */
function questionIndicesAreZeroBased(allQuestions) {
  if (!Array.isArray(allQuestions) || allQuestions.length === 0) {
    return null;
  }

  const nums = allQuestions
    .map((q) => Number(q?.question_index))
    .filter(Number.isFinite);

  if (nums.length === 0) {
    return null;
  }

  return Math.min(...nums) === 0;
}

export function questionDisplayIndex(item, arrayIndex, allQuestions) {
  const n = Number(item?.question_index);
  const i = Number(arrayIndex);

  if (!Number.isFinite(n)) {
    return (Number.isFinite(i) && i >= 0 ? i : 0) + 1;
  }

  const zeroBased = questionIndicesAreZeroBased(allQuestions);

  if (zeroBased === true) {
    return n >= 0 ? n + 1 : (Number.isFinite(i) && i >= 0 ? i : 0) + 1;
  }

  // 1-based or unknown (null): preserve legacy behaviour
  if (Number.isFinite(i) && i >= 0) {
    if (n === i) {
      return n + 1;
    }

    if (n === i + 1) {
      return n;
    }
  }

  return n >= 0 ? n + 1 : (Number.isFinite(i) && i >= 0 ? i : 0) + 1;
}
